import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from '../../toasts';
import { useLocation, useNavigate } from 'react-router-dom';
import CreditService from '../../services/credit.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import {
	Section,
	StatStrip,
	DataTable,
	IdChip,
	Pill,
	RowActionsMenu,
	Button,
} from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function creditEstado(credit) {
	if ((credit.saldo || 0) <= 0 && credit.cuotasTotal > 0) {
		return 'pagada';
	}
	if (credit.endDate && moment.utc(credit.endDate).isBefore(moment.utc())) {
		return 'vencida';
	}
	return 'activa';
}

const ESTADO_TONE = { pagada: 'ok', vencida: 'danger', activa: 'accent' };
const ESTADO_LABEL = { pagada: 'Pagada', vencida: 'Vencida', activa: 'Activa' };

function AccountCredits({ accountId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();
	const [deletingCredit, setDeletingCredit] = useState(null);

	const returnTo = () => encodeURIComponent(location.pathname + location.search);

	const handleCreateCredit = () => {
		navigate(`/AdminScreen/accounts/account/${accountId}/newCredit?returnTo=${returnTo()}`);
	};

	const handleEditCredit = (credit) => {
		navigate(`/AdminScreen/credits/editCredit/${credit._id}?returnTo=${returnTo()}`);
	};

	const stats = useMemo(() => {
		const pendientes = rows.filter((credit) => creditEstado(credit) !== 'pagada').length;
		const totalByCurrency = new Map();
		rows.forEach((credit) => {
			if (creditEstado(credit) !== 'pagada') {
				const key = credit.currency || '—';
				totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (credit.saldo || 0));
			}
		});
		return [
			{ label: 'Pendientes', value: pendientes },
			{
				label: 'Total pendiente',
				value: [...totalByCurrency.entries()].map(([currency, saldo]) => formatMoney(currency, saldo)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows]);

	async function handleConfirmDelete() {
		try {
			await CreditService.delete(deletingCredit._id);
			toast.success('Pasividad Eliminada');
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingCredit(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (credit) => <IdChip>{credit.customId}</IdChip> },
		{ key: 'concept', header: 'Concepto', render: (credit) => credit.concept },
		{ key: 'date', header: 'Período', render: (credit) => formatDate(credit.date) },
		{ key: 'endDate', header: 'Vence', render: (credit) => formatDate(credit.endDate) },
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right',
			render: (credit) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(credit.currency, credit.amount)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (credit) => {
				const estado = creditEstado(credit);
				return <Pill tone={ESTADO_TONE[estado]}>{ESTADO_LABEL[estado]}</Pill>;
			},
		},
	];

	const renderRowActions = (credit) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => handleEditCredit(credit) },
				{ label: 'Eliminar', onClick: () => setDeletingCredit(credit), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={handleCreateCredit}>
					Nueva pasividad
				</Button>
			</div>
			<Section title='Pasividades'>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(credit) => navigate(`/AdminScreen/credits/credit/${credit._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron pasividades para esta cuenta.'
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingCredit)}
				handleClose={() => setDeletingCredit(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteCredit')}
				description={t('msg.confirmDeleteCredit')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AccountCredits;
